import { useCallback, useEffect, useRef, useState } from 'react'

/**
 * How long since the user last did anything.
 *
 * REAL ACTIVITY, NOT TOKEN REFRESHES. The app's existing session timer measures time since the
 * last token refresh, which keeps ticking while someone reads the screen and stops while a
 * background request fires. That is fine for "is this token stale"; it is the wrong signal for
 * "has this person walked away".
 *
 * Listeners are passive and on the capture phase so nothing can swallow them, and the timestamp is
 * a ref rather than state: a mousemove must not re-render the application.
 */

const ACTIVITY_EVENTS = ['mousemove', 'mousedown', 'keydown', 'wheel', 'touchstart', 'scroll'] as const

export function useIdleTimer({
  thresholds, enabled = true, pollMs = 5000,
}: {
  /** Named thresholds in milliseconds, e.g. { screensaver: 600000, logout: 28800000 }. */
  thresholds: Record<string, number>
  enabled?: boolean
  pollMs?: number
}) {
  const lastActivity = useRef(Date.now())
  const [reached, setReached] = useState<Record<string, boolean>>({})

  const markActive = useCallback(() => { lastActivity.current = Date.now() }, [])

  useEffect(() => {
    if (!enabled) return
    // THE CLOCK STARTS WHEN THE TIMER DOES. While the timer is disabled — nobody signed in — the
    // listeners below are detached, so nothing refreshes `lastActivity`, but the clock it is
    // measured against keeps running. A login page left open longer than the screensaver threshold
    // therefore made the FIRST tick after signing in already over the line, and the router locked a
    // session one second old. Idle time is time a session sat untouched; there was no session.
    lastActivity.current = Date.now()
    const onActivity = () => { lastActivity.current = Date.now() }
    for (const ev of ACTIVITY_EVENTS) {
      window.addEventListener(ev, onActivity, { passive: true, capture: true })
    }
    // Coming back to a tab is activity; leaving it is not, or a screen left on a wall would never
    // reach its threshold while the browser throttled the timer.
    const onVisible = () => { if (document.visibilityState === 'visible') lastActivity.current = Date.now() }
    document.addEventListener('visibilitychange', onVisible)
    return () => {
      for (const ev of ACTIVITY_EVENTS) window.removeEventListener(ev, onActivity, { capture: true })
      document.removeEventListener('visibilitychange', onVisible)
    }
  }, [enabled])

  useEffect(() => {
    if (!enabled) { setReached({}); return }
    const tick = () => {
      const idleFor = Date.now() - lastActivity.current
      setReached((prev) => {
        let changed = false
        const next: Record<string, boolean> = {}
        for (const [key, ms] of Object.entries(thresholds)) {
          next[key] = ms > 0 && idleFor >= ms
          if (next[key] !== prev[key]) changed = true
        }
        // Only a real transition updates state — polling every few seconds must not re-render.
        return changed ? next : prev
      })
    }
    tick()
    const id = setInterval(tick, pollMs)
    return () => clearInterval(id)
  }, [enabled, pollMs, JSON.stringify(thresholds)])

  return { reached, markActive, idleSince: lastActivity }
}
